import { forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Box, Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, rem, Textarea } from '@bonusly/design-system';

import { FormError } from '../../components/common/form';

interface ReportBonusModalProps {
	isLoading?: boolean;
	isOpen: boolean;
	onClose: VoidFunction;
	onConfirm: (data: FormState) => void;
}

export interface FormState {
	reason: string;
}

export interface ReportBonusModalActions {
	close: VoidFunction;
}

export const ReportBonusModal = forwardRef<ReportBonusModalActions, ReportBonusModalProps>(({ isLoading, isOpen, onClose, onConfirm }, ref) => {
	const { t } = useTranslation(['recognition', 'common']);

	const {
		formState: { errors },
		handleSubmit,
		register,
		reset,
	} = useForm<FormState>();

	const handleClose = () => {
		onClose();
		reset();
	};

	useImperativeHandle(ref, () => ({
		close: handleClose,
	}));

	return (
		<Modal isCentered isOpen={isOpen} onClose={handleClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<Box as="form" onSubmit={handleSubmit(onConfirm)}>
					<ModalHeader borderBottomWidth={rem(1)} borderColor="border.default" borderStyle="solid" color="text.default">
						{t('report_bonus.header', { ns: 'recognition' })}
					</ModalHeader>
					<ModalCloseButton size="lg" borderRadius="full" />
					<ModalBody>
						<Textarea
							_invalid={{ borderColor: 'red.600', borderWidth: '2px' }}
							isInvalid={!!errors.reason}
							placeholder={
								t('report_bonus.input_placeholder', {
									ns: 'recognition',
								}) ?? ''
							}
							{...register('reason', {
								required:
									t('report_bonus.validation.reason_required', {
										ns: 'recognition',
									}) ?? '',
							})}
						/>

						{errors.reason?.message && <FormError error={errors.reason.message} mt={2} />}
					</ModalBody>

					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={onClose}>
							{t('cancel', { ns: 'common' })}
						</Button>

						<Button isLoading={isLoading} type="submit">
							{t('report_bonus.submit_button', { ns: 'recognition' })}
						</Button>
					</ModalFooter>
				</Box>
			</ModalContent>
		</Modal>
	);
});

ReportBonusModal.displayName = 'ReportBonusModal';
